import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, inject } from '@angular/core';
import { FormBuilder, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { Subscription, finalize } from 'rxjs';
import { DateOnly, SURGEON, ToastService, US_CODE, convertToLocalDate, dateFormat } from '../../core';
import { IProfileControl, ProfileControl, ProfileForm, profileErrorMsgs, setProfileValidators } from '../../models';
import { UserService } from '../../services';
import { BaseComponent, ConfirmationComponent, DatepickerComponent, FormValidationMsgDirective } from '../../shared';


/**
 * Edit account information component
 */
@Component({
	selector: 'profile-form',
	templateUrl: './profile-form.component.html',
	imports: [
		FormsModule,
		ReactiveFormsModule,
		DatepickerComponent,
		FormValidationMsgDirective
	],
	changeDetection: ChangeDetectionStrategy.Eager
})
export class ProfileFormComponent extends BaseComponent implements OnInit, OnDestroy {

	private readonly userSrv = inject(UserService);
	private readonly toastSrv = inject(ToastService);
	private readonly modalSrv = inject(NgbModal);
	private readonly formBuilder = inject(FormBuilder);

	isSurgeon: boolean = false;
	allUsStates: string[] = [];
	isUnitedStates: boolean = false;
	isSaving: boolean = false;

	readonly profileForm = this.formBuilder.nonNullable.group<IProfileControl>(new ProfileControl());
	readonly maxDateOfBirth: Date = new Date(new Date().setFullYear(new Date().getFullYear() - 18));
	readonly errorMsgs = profileErrorMsgs;
	readonly dateFormat = dateFormat();

	private usStateSubscription?: Subscription;

	ngOnInit(): void {
		setProfileValidators(this.profileForm);
		this.initProfileForm();
	}

	private initProfileForm(): void {
		this.userSrv.getProfileForm().subscribe(form => {
			this.profileForm.setValue(form);
			this.isSurgeon = form.roleInfo === SURGEON;
			this.isUnitedStates = form.country.code === US_CODE;
			if (this.isUnitedStates) this.initUsStatesList();
		});
	}

	private initUsStatesList(): void {
		this.usStateSubscription = this.userSrv.getUsStatesList().subscribe(list => this.allUsStates = list);
	}

	/**
	* Update date of birth string format.
	*/
	updateDateOfBirth(date: Date | null): void {
		this.profileForm.controls.dateOfBirth.setValue(date ? new DateOnly(date) : null);
		this.profileForm.controls.dateOfBirthFormat.setValue(convertToLocalDate(date));
		this.profileForm.controls.dateOfBirth.markAsTouched();
	}

	/**
	* Reset date of birth.
	*/
	clearDateOfBirth(): void {
		this.profileForm.controls.dateOfBirth.setValue(null);
		this.profileForm.controls.dateOfBirth.markAsTouched();
		this.profileForm.controls.dateOfBirthFormat.setValue(null);
	}

	/**
	* Open modal to confirm editin profile.
	*/
	openConfirmModal() {
		if (!this.modalSrv.hasOpenModals()) {
			const modalRef: NgbModalRef = this.modalSrv.open(ConfirmationComponent, { centered: true, backdrop: 'static' });
			(modalRef.componentInstance as ConfirmationComponent).config = {
				title: this.labels().PROFILE_FORM_COMPONENT_CONFIRMATION_TITLE,
				message: this.labels().PROFILE_FORM_COMPONENT_CONFIRMATION_MESSAGE
			};
			modalRef.result.then(() => this.submitForm()).catch(error => false);
		}
	}

	/**
	* Submit edit profile form.
	*/
	submitForm(): void {
		this.isSaving = true;
		this.userSrv.editProfileForm(this.profileForm.value as ProfileForm).pipe(
			finalize(() => this.isSaving = false)
		).subscribe({
			next: () => {
				this.toastSrv.showSuccess(this.labels().PROFILE_FORM_COMPONENT_SUCCESS_MESSAGE);
				this.returnToPreviousPage();
			},
			error: () => this.toastSrv.showError(this.labels().PROFILE_FORM_COMPONENT_ERROR_MESSAGE)
		});
	}

	/**
	* Navigate to previous page.
	*/
	returnToPreviousPage(): void {
		history.back();
	}


	ngOnDestroy(): void {
		this.usStateSubscription?.unsubscribe();
	}

}
